"use client";

import { NextIntlClientProvider, type AbstractIntlMessages } from "next-intl";
import { ThemeProvider } from "next-themes";

import type { AppLocale } from "../i18n/routing";
import type { DefModelCnfState } from "../lib/api/filter-options";
import { DefModelCnfProvider } from "../context/DefModelCnfContext";
import { UserProvider } from "../context/UserContext";
import { Toaster } from "../components/ui/sonner";
import { PreferencesStoreProvider } from "../stores/preferences/preferences-provider";
import type { PreferencesState } from "../stores/preferences/preferences-store";
import { TooltipProvider } from "./ui/tooltip";
import { RadarAnalytics } from "./Radar-analysis";

type ProvidersProps = {
  children: React.ReactNode;
  locale: AppLocale;
  messages: AbstractIntlMessages;
  defModelCnf: DefModelCnfState | null;
  themePreset: string;
  font: string;
  contentLayout: string;
  navbarStyle: string;
  sidebarVariant: string;
  sidebarCollapsible: string;
};

export function Providers({
  children,
  locale,
  messages,
  defModelCnf,
  themePreset,
  font,
  contentLayout,
  navbarStyle,
  sidebarVariant,
  sidebarCollapsible,
}: ProvidersProps) {
  return (
    <NextIntlClientProvider
      locale={locale}
      messages={messages}
      timeZone="Asia/Shanghai"
    >
      <ThemeProvider
        attribute="class"
        defaultTheme="light"
        enableSystem
        disableTransitionOnChange
      >
        <PreferencesStoreProvider
          themePreset={themePreset as PreferencesState["themePreset"]}
          font={font as PreferencesState["font"]}
          contentLayout={contentLayout as PreferencesState["contentLayout"]}
          navbarStyle={navbarStyle as PreferencesState["navbarStyle"]}
          sidebarVariant={sidebarVariant as PreferencesState["sidebarVariant"]}
          sidebarCollapsible={
            sidebarCollapsible as PreferencesState["sidebarCollapsible"]
          }
        >
          <TooltipProvider>
            <UserProvider>
              <DefModelCnfProvider value={defModelCnf}>
                {children}
              </DefModelCnfProvider>
            </UserProvider>
          </TooltipProvider>
        </PreferencesStoreProvider>
        <RadarAnalytics />
        <Toaster richColors position="top-right" />
      </ThemeProvider>
    </NextIntlClientProvider>
  );
}
